import * as THREE from 'three'
import { Section } from './Section.js'
import { LiquidGridPlane } from '../core/LiquidGridPlane.js'

// Text reveal effect — randomised character cycle before locking in
export class TextReveal {
  constructor(element, direction = 'center-out') {
    this.element = element
    this.direction = direction
    this.originalText = element.textContent
    this.chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890!@#$%^&*()-_=+{}|[]\\;\':"<>?,./`~'.split('')
    this.done = false
    this.cycleCount = 2
    this.cycleCurrent = 0
    this.letterCurrent = 0
    this.letters = []
    this.rafId = null
    this.init()
  }

  init() {
    this.element.innerHTML = ''
    this.letters = []
    for (let i = 0; i < this.originalText.length; i++) {
      const char = this.originalText[i]
      const span = document.createElement('span')
      span.textContent = char
      span.dataset.orig = char
      if (char === ' ') {
        span.style.width = '0.3em'
      }
      this.element.appendChild(span)
      this.letters.push(span)
    }
  }

  getChar() {
    return this.chars[Math.floor(Math.random() * this.chars.length)]
  }

  reset() {
    this.done = false
    this.cycleCurrent = 0
    if (this.rafId) cancelAnimationFrame(this.rafId)

    if (this.direction === 'left-to-right') {
      this.revealLeft = 0
      this.revealRight = null
    } else {
      // Start from the middle and expand outward
      const mid = Math.floor(this.letters.length / 2)
      this.revealLeft = mid
      this.revealRight = mid + 1
    }

    this.letters.forEach(span => {
      span.textContent = '-'
      span.classList.remove('done')
    })

    this.loop()
  }

  loop() {
    this.letters.forEach((span) => {
      if (!span.classList.contains('done')) {
        if (span.dataset.orig !== ' ') {
          span.textContent = this.getChar()
          span.style.opacity = (Math.random() * 0.5 + 0.3).toString()
        }
      }
    })

    if (this.cycleCurrent < this.cycleCount) {
      this.cycleCurrent++
    } else if (this.direction === 'left-to-right') {
      // Lock in one character at a time, left to right
      if (this.revealLeft < this.letters.length) {
        const currLetter = this.letters[this.revealLeft]
        currLetter.textContent = currLetter.dataset.orig
        currLetter.style.opacity = '1'
        currLetter.classList.add('done')
        this.revealLeft++
      } else {
        this.done = true
      }
      this.cycleCurrent = 0
    } else {
      // Lock in left side (from middle outward)
      if (this.revealLeft >= 0) {
        const leftLetter = this.letters[this.revealLeft]
        leftLetter.textContent = leftLetter.dataset.orig
        leftLetter.style.opacity = '1'
        leftLetter.classList.add('done')
        this.revealLeft--
      }
      // Lock in right side (from middle outward)
      if (this.revealRight < this.letters.length) {
        const rightLetter = this.letters[this.revealRight]
        rightLetter.textContent = rightLetter.dataset.orig
        rightLetter.style.opacity = '1'
        rightLetter.classList.add('done')
        this.revealRight++
      }
      this.cycleCurrent = 0

      if (this.revealLeft < 0 && this.revealRight >= this.letters.length) {
        this.done = true
      }
    }

    if (!this.done) {
      this.rafId = requestAnimationFrame(() => this.loop())
    }
  }

  restore() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.element.textContent = this.originalText
    this.letters = []
  }
}

// Carousel configuration
const CAROUSEL_CONFIG = {
  imageCount: 12,
  spacing: 0.5,
  height: 3,
  radius: 6,
  rotationDuration: 20, // seconds for full rotation
  yOffset: 0,
  zOffset: 0,
  planeColor: 0x4488ff,
  emissiveColor: 0x2244aa
}

// Texture fade duration
const TEXTURE_FADE_DURATION = 0.6 // seconds

// Camera target for experience section
const EXPERIENCE_CAMERA_Z = -45
const SETTLE_DELAY = 0.25 // seconds to wait after camera settles
const FADE_DURATION = 0.8 // seconds for fade in animation

export class ExperienceSection extends Section {
  async build(camera, renderer) {
    console.log('🏗️ ExperienceSection.build() called')
    super.build()
    
    // Store camera and renderer
    this.camera = camera
    this.renderer = renderer
    this.bgFadeProgress = 0
    console.log('🏗️ After super.build(), group:', !!this.group, 'isActive:', this.isActive)
    
    // Bind handlers FIRST (before using them)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)

    
    // Background - Image texture
    this.createTexturedBackground()
    
    // Create liquid grid plane beneath the carousel
    this.createLiquidGrid()
    
    // Create carousel (async - loads texture atlas)
    await this.createCarousel()
    
    // Fade-in state
    this.cameraSettledTime = null
    this.carouselFadedIn = false
    this.carouselOpacity = 0
    
    // Per-instance carousel scale animation
    if (this.carouselMesh) {
      this.carouselInstanceScales = new Float32Array(this.carouselMesh.count).fill(0)
    }
    this.carouselAnimTime = 0
    this.isExiting = false
    this.exitAnimTime = 0
    
    // Toggle state
    this.currentMode = 'brands' // 'brands' or 'personal'
    this.toggleElement = document.getElementById('experience-toggle')
    this.initToggle()
    
    // Texture fade state
    this.textureFadeTarget = 0.0 // 0 = brands, 1 = personal
    this.textureFadeCurrent = 0.0
    
    // Frame sequence plane (texture atlas)
    this.framePlane = null
    this.frameTargetProgress = 0.0 // 0.0 = brands (start), 1.0 = personal (end)
    this.frameCurrentProgress = 0.0
    this.frameAtlasTexture = null
    this.frameAtlasData = null
    this.initFramePlane()
  }
  
  initToggle() {
    if (!this.toggleElement) return
    
    // Bind toggle click handlers
    const options = this.toggleElement.querySelectorAll('.toggle-option')
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        const mode = e.target.dataset.mode
        if (mode && mode !== this.currentMode) {
          this.setMode(mode)
        }
      })
    })
  }
  
  setMode(mode) {
    if (!this.toggleElement) return
    
    this.currentMode = mode
    
    // Update UI
    const options = this.toggleElement.querySelectorAll('.toggle-option')
    options.forEach(option => {
      if (option.dataset.mode === mode) {
        option.classList.add('active')
      } else {
        option.classList.remove('active')
      }
    })
    
    // Update frame target position
    // brands = 0.0 (start frame)
    // personal = 1.0 (end frame)
    this.frameTargetProgress = mode === 'personal' ? 1.0 : 0.0
    
    // Trigger texture fade
    this.textureFadeTarget = mode === 'personal' ? 1.0 : 0.0
    
    console.log('Experience mode changed to:', mode)
  }
  
  showToggle() {
    console.log('🔘 showToggle() called, current section index:', this.sectionIndex, 'isActive:', this.isActive)
    if (this.toggleElement) {
      this.toggleElement.classList.add('visible')
    }
  }
  
  hideToggle() {
    console.log('🔘 hideToggle() called, current section index:', this.sectionIndex, 'isActive:', this.isActive)
    if (this.toggleElement) {
      this.toggleElement.classList.remove('visible')
    }
  }
  
  showExperienceHeader() {
    const header = document.getElementById('experience-header')
    if (header) header.classList.add('visible')
    
    const textEl = document.querySelector('.experience-header-text')
    if (textEl) {
      if (!this.headerTextReveal) {
        this.headerTextReveal = new TextReveal(textEl)
      }
      // Small delay so the container fade-in has started
      setTimeout(() => this.headerTextReveal.reset(), 100)
    }
  }
  
  hideExperienceHeader() {
    const header = document.getElementById('experience-header')
    if (header) header.classList.remove('visible')
    
    if (this.headerTextReveal) {
      this.headerTextReveal.restore()
      this.headerTextReveal = null
    }
  }
  
  createTexturedBackground() {
    const textureLoader = new THREE.TextureLoader()
    const texture = textureLoader.load('./sect03/expirence-bg.jpg')
    texture.colorSpace = THREE.SRGBColorSpace

    // Lock background plane to 1440:900 aspect ratio
    const targetAspect = 1440 / 900
    const aspect = window.innerWidth / window.innerHeight

    // Calculate dimensions to cover the viewport while maintaining aspect ratio
    const scale = 1.0
    let width, height
    if (aspect > targetAspect) {
      // Viewport is wider than image
      width = 20 * aspect * scale
      height = width / targetAspect
    } else {
      // Viewport is taller than image
      height = 20 * scale
      width = height * targetAspect
    }

    const geometry = new THREE.PlaneGeometry(width, height)
    this.bgMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide
    })

    this.backgroundPlane = new THREE.Mesh(geometry, this.bgMaterial)
    this.scene.add(this.backgroundPlane)
  }

  createLiquidGrid() {
    console.log('🔧 createLiquidGrid called, camera:', !!this.camera, 'renderer:', !!this.renderer, 'group:', !!this.group)
    if (!this.camera || !this.renderer || !this.group) {
      console.log('❌ Missing required objects for liquid grid')
      return
    }
    
    console.log('✅ Creating liquid grid...')
    this.liquidGrid = new LiquidGridPlane(this.group, {
      camera: this.camera,
      renderer: this.renderer
    })
    console.log('✅ Liquid grid created:', !!this.liquidGrid)
  }

  onResize() {
    if (!this.backgroundPlane) return

    const targetAspect = 1440 / 900
    const aspect = window.innerWidth / window.innerHeight

    const scale = 1.0
    let width, height
    if (aspect > targetAspect) {
      width = 20 * aspect * scale
      height = width / targetAspect
    } else {
      height = 20 * scale
      width = height * targetAspect
    }

    this.backgroundPlane.geometry.dispose()
    this.backgroundPlane.geometry = new THREE.PlaneGeometry(width, height)
  }
  
  onMouseLeave() {
    if (this.liquidGrid) this.liquidGrid.setMouseOver(false)
  }
  
onMouseMove(event) {
    if (this.liquidGrid && this.isActive) {
      this.liquidGrid.updateMouse(event.clientX, event.clientY)
      this.liquidGrid.setMouseOver(true)
    }
  }
  
  async initFramePlane() {
    try {
      // Load atlas metadata
      const response = await fetch('./sect03/meditate_atlas.json')
      this.frameAtlasData = await response.json()
      
      // Load atlas texture
      const textureLoader = new THREE.TextureLoader()
      this.frameAtlasTexture = await new Promise((resolve, reject) => {
        textureLoader.load(
          './sect03/meditate_atlas.webp',
          (texture) => {
            texture.colorSpace = THREE.SRGBColorSpace
            texture.minFilter = THREE.LinearFilter
            texture.magFilter = THREE.LinearFilter
            resolve(texture)
          },
          undefined,
          reject
        )
      })
      
      this.createFramePlane()
    } catch (err) {
      console.warn('Frame atlas not found. Run: node scripts/process-frames.js')
      // Fallback: create placeholder
      this.createFramePlane(true)
    }
  }
  
  createFramePlane(isFallback = false) {
    const geometry = new THREE.PlaneGeometry(1, 1)
    
    if (isFallback || !this.frameAtlasTexture) {
      // Fallback: simple colored plane
      this.frameMaterial = new THREE.MeshBasicMaterial({
        color: 0x4488ff,
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide
      })
      this.frameMaterial.toneMapped = false
    } else {
      // Shader material for frame atlas
      const atlasData = this.frameAtlasData
      
      this.frameMaterial = new THREE.ShaderMaterial({
        uniforms: {
          atlasTexture: { value: this.frameAtlasTexture },
          uvOffset: { value: new THREE.Vector2(0, 0) },
          uvScale: { value: new THREE.Vector2(1 / atlasData.gridCols, 1 / atlasData.gridRows) },
          opacity: { value: 1.0 }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D atlasTexture;
          uniform vec2 uvOffset;
          uniform vec2 uvScale;
          uniform float opacity;
          varying vec2 vUv;
          
          void main() {
            vec2 atlasUv = uvOffset + vUv * uvScale;
            vec4 color = texture2D(atlasTexture, atlasUv);
            if (color.a < 0.01) discard;
            gl_FragColor = vec4(color.rgb, color.a * opacity);
          }
        `,
        transparent: true,
        side: THREE.DoubleSide
      })
      this.frameMaterial.toneMapped = false
    }
    
    this.framePlane = new THREE.Mesh(geometry, this.frameMaterial)
    this.framePlane.position.set(0, -0.8, 0)
    this.framePlane.scale.set(4.5, 4.5, 1)  // Scaled up 50% from 3 to 4.5
    this.framePlane.name = 'framePlane'
    
    this.add(this.framePlane)
    
    // Set initial frame
    this.updateFrameProgress(0.0)
  }
  
  updateFrameProgress(progress) {
    if (!this.frameMaterial || !this.frameAtlasData) return
    
    // Clamp progress
    progress = Math.max(0, Math.min(1, progress))
    
    const { frameCount, gridCols, gridRows } = this.frameAtlasData
    
    // Calculate frame index
    const frameIndex = Math.floor(progress * (frameCount - 1))
    const col = frameIndex % gridCols
    const row = Math.floor(frameIndex / gridCols)
    
    // Calculate UV offset
    const uvOffsetX = col / gridCols
    const uvOffsetY = 1.0 - ((row + 1) / gridRows) // Flip Y
    
    this.frameMaterial.uniforms.uvOffset.value.set(uvOffsetX, uvOffsetY)
  }
  
  async createCarousel() {
    const { spacing, height, yOffset, zOffset } = CAROUSEL_CONFIG
    
    // Create a group for the carousel
    this.carouselGroup = new THREE.Group()
    this.carouselGroup.position.set(0, yOffset, zOffset)
    this.add(this.carouselGroup)
    
    // Load atlas data for both textures
    let brandsData = null
    let personalData = null
    let imageCount = CAROUSEL_CONFIG.imageCount
    
    try {
      const [brandsResponse, personalResponse] = await Promise.all([
        fetch('./sect03/renders.json'),
        fetch('./sect03/personal.json')
      ])
      brandsData = await brandsResponse.json()
      personalData = await personalResponse.json()
      imageCount = brandsData.frames.length
      console.log('🎠 Both atlases loaded:', imageCount, 'frames')
    } catch (err) {
      console.error('🎠 Failed to load atlas JSON:', err)
      // Fallback: try to load just brands
      try {
        const response = await fetch('./sect03/renders.json')
        brandsData = await response.json()
        imageCount = brandsData.frames.length
      } catch (e) {
        console.error('🎠 Failed to load renders.json:', e)
      }
    }
    
    // Store atlas data for UV updates during fade
    this.brandsAtlasData = brandsData
    this.personalAtlasData = personalData
    
    // Calculate plane dimensions and radius
    const planeWidth = height * 0.75
    const radius = (imageCount * (planeWidth + spacing)) / (Math.PI * 2)
    
    // Create geometry for panels
    const planeGeo = new THREE.PlaneGeometry(planeWidth, height)
    
    // Load both texture atlases
    const textureLoader = new THREE.TextureLoader()
    
    const brandsTexture = textureLoader.load('./sect03/renders.webp')
    brandsTexture.colorSpace = THREE.SRGBColorSpace
    brandsTexture.flipY = false
    
    const personalTexture = textureLoader.load('./sect03/personal.webp')
    personalTexture.colorSpace = THREE.SRGBColorSpace
    personalTexture.flipY = false
    
    this.brandsTexture = brandsTexture
    this.personalTexture = personalTexture
    
    // Get atlas sizes from metadata or use default
    const brandsAtlasSize = brandsData?.meta?.size 
      ? new THREE.Vector2(brandsData.meta.size.w, brandsData.meta.size.h)
      : new THREE.Vector2(2048, 2048)
    
    const personalAtlasSize = personalData?.meta?.size
      ? new THREE.Vector2(personalData.meta.size.w, personalData.meta.size.h)
      : new THREE.Vector2(2048, 2048)
    
    // Create shader material for atlas with unlit output and texture fade support
    const planeMat = new THREE.ShaderMaterial({
      uniforms: {
        brandsTexture: { value: brandsTexture },
        personalTexture: { value: personalTexture },
        brandsAtlasSize: { value: brandsAtlasSize },
        personalAtlasSize: { value: personalAtlasSize },
        opacity: { value: 1 },
        textureMix: { value: 0.0 }, // 0 = brands, 1 = personal
        emissiveIntensity: { value: 1 }
      },
      vertexShader: `
        attribute vec4 instanceUvRect;
        attribute vec4 instancePersonalUvRect;
        varying vec2 vUv;
        varying vec4 vUvRect;
        varying vec4 vPersonalUvRect;
        
        void main() {
          vUv = uv;
          vUvRect = instanceUvRect;
          vPersonalUvRect = instancePersonalUvRect;
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D brandsTexture;
        uniform sampler2D personalTexture;
        uniform vec2 brandsAtlasSize;
        uniform vec2 personalAtlasSize;
        uniform float opacity;
        uniform float textureMix;
        uniform float emissiveIntensity;
        varying vec2 vUv;
        varying vec4 vUvRect;
        varying vec4 vPersonalUvRect;
        
        void main() {
          // Sample brands texture
          vec2 brandsUvOffset = vUvRect.xy / brandsAtlasSize;
          vec2 brandsUvScale = vUvRect.zw / brandsAtlasSize;
          vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
          vec2 brandsAtlasUV = brandsUvOffset + uv * brandsUvScale;
          vec4 brandsColor = texture2D(brandsTexture, brandsAtlasUV);
          
          // Sample personal texture
          vec2 personalUvOffset = vPersonalUvRect.xy / personalAtlasSize;
          vec2 personalUvScale = vPersonalUvRect.zw / personalAtlasSize;
          vec2 personalAtlasUV = personalUvOffset + uv * personalUvScale;
          vec4 personalColor = texture2D(personalTexture, personalAtlasUV);
          
          // Mix between textures based on textureMix
          vec4 color = mix(brandsColor, personalColor, textureMix);
          
          if (color.a < 0.1) discard;
          
          color.a *= opacity;
          
          // Emissive boost — self-illuminated, unlit look
          color.rgb *= emissiveIntensity;
          gl_FragColor = color;
          #include <colorspace_fragment>
        }
      `,
      transparent: true,
      side: THREE.FrontSide
    })
    
    // Create instanced mesh
    this.carouselMesh = new THREE.InstancedMesh(planeGeo, planeMat, imageCount)
    this.carouselMesh.name = 'carouselInstanced'
    
    const dummy = new THREE.Object3D()
    const brandsUvRects = []
    const personalUvRects = []
    
    for (let i = 0; i < imageCount; i++) {
      const angle = (i / imageCount) * Math.PI * 2
      
      dummy.position.set(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius
      )
      dummy.lookAt(0, 0, 0)
      dummy.updateMatrix()
      
      this.carouselMesh.setMatrixAt(i, dummy.matrix)
      
      // Brands UV rects
      if (brandsData && brandsData.frames[i]) {
        const frame = brandsData.frames[i].frame
        brandsUvRects.push(frame.x, frame.y, frame.w, frame.h)
      } else {
        const cols = Math.ceil(Math.sqrt(imageCount))
        const col = i % cols
        const row = Math.floor(i / cols)
        const cellW = brandsAtlasSize.x / cols
        const cellH = brandsAtlasSize.y / Math.ceil(imageCount / cols)
        brandsUvRects.push(col * cellW, row * cellH, cellW, cellH)
      }
      
      // Personal UV rects
      if (personalData && personalData.frames[i]) {
        const frame = personalData.frames[i].frame
        personalUvRects.push(frame.x, frame.y, frame.w, frame.h)
      } else {
        // Fallback: use same as brands if personal data not available
        if (brandsData && brandsData.frames[i]) {
          const frame = brandsData.frames[i].frame
          personalUvRects.push(frame.x, frame.y, frame.w, frame.h)
        } else {
          const cols = Math.ceil(Math.sqrt(imageCount))
          const col = i % cols
          const row = Math.floor(i / cols)
          const cellW = (personalData?.meta?.size?.w || 2048) / cols
          const cellH = (personalData?.meta?.size?.h || 2048) / Math.ceil(imageCount / cols)
          personalUvRects.push(col * cellW, row * cellH, cellW, cellH)
        }
      }
    }
    
    planeGeo.setAttribute('instanceUvRect',
      new THREE.InstancedBufferAttribute(new Float32Array(brandsUvRects), 4))
    planeGeo.setAttribute('instancePersonalUvRect',
      new THREE.InstancedBufferAttribute(new Float32Array(personalUvRects), 4))
    
    this.carouselMesh.instanceMatrix.needsUpdate = true
    this.carouselMesh.material.toneMapped = false
    this.carouselGroup.add(this.carouselMesh)
    
    // Store base matrices for per-instance scale animation
    this.carouselBaseMatrices = []
    for (let i = 0; i < imageCount; i++) {
      const m = new THREE.Matrix4()
      this.carouselMesh.getMatrixAt(i, m)
      this.carouselBaseMatrices.push(m)
    }
    
    // Add center glow/light
    const centerLight = new THREE.PointLight(0x4488ff, 2, 15)
    centerLight.position.set(0, 0, 0)
    this.carouselGroup.add(centerLight)
    
    this.createRings(radius)
  }
  
  createRings(radius) {
    // Inner ring only (still / no animation)
    const ringGeo = new THREE.TorusGeometry(radius * 0.35, 0.015, 8, 48)
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x000000,
      emissive: 0xffffff,
      emissiveIntensity: 3.0
    })
    const ring = new THREE.Mesh(ringGeo, ringMat)
    ring.position.y = 0.15
    ring.position.z = -2
    ring.scale.setScalar(1.25)
    this.carouselGroup.add(ring)
  }
  
  onEnter() {
    super.onEnter()
    console.log('🚪 ExperienceSection.onEnter() called')
    
    // Show experience header
    this.showExperienceHeader()
    
    // Cancel any pending exit animation
    this.isExiting = false
    
    // Reset fade-in state for carousel planes only
    this.cameraSettledTime = null
    this.carouselFadedIn = false
    this.carouselOpacity = 0
    this.bgFadeProgress = 0
    
    // Reset per-instance scale animation
    this.carouselAnimTime = 0
    if (this.carouselInstanceScales) {
      this.carouselInstanceScales.fill(0)
    }
    if (this.carouselMesh && this.carouselBaseMatrices) {
      const dummy = new THREE.Object3D()
      const pos = new THREE.Vector3()
      const quat = new THREE.Quaternion()
      const scale = new THREE.Vector3()
      for (let i = 0; i < this.carouselMesh.count; i++) {
        this.carouselBaseMatrices[i].decompose(pos, quat, scale)
        dummy.position.copy(pos)
        dummy.quaternion.copy(quat)
        dummy.scale.set(0, 0, 0)
        dummy.updateMatrix()
        this.carouselMesh.setMatrixAt(i, dummy.matrix)
      }
      this.carouselMesh.instanceMatrix.needsUpdate = true
    }
    
    // Show liquid grid when entering
    if (this.liquidGrid) {
      this.liquidGrid.setVisibility(true)
    }
    
    // Add mouse listeners when entering section
    console.log('🎯 Adding mouse listeners')
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseleave', this.onMouseLeave)

    
    // Show toggle with delay
    this.showToggleTimeout = setTimeout(() => this.showToggle(), 1000)
  }
  
  update(delta, scrollProgress, cameraZ, isCameraSettled) {
    // Handle exit animation even when not active
    if (this.isExiting) {
      this.handleExitAnimation(delta)
      return
    }
    
    if (!this.isActive) return
    
    const time = performance.now() * 0.001
    
    // Handle fade-in logic
    this.handleFadeIn(isCameraSettled, cameraZ, delta)
    
    // Update liquid grid simulation (pass time for auto-ripples when idle)
    if (this.liquidGrid) {
      this.liquidGrid.update(performance.now(), delta)
    }
    
    // Update frame scrub position
    this.updateFrameScrub(delta)
    
    // Update texture fade
    this.updateTextureFade(delta)
    
    // Rotate carousel
    if (this.carouselMesh) {
      const rotationSpeed = (Math.PI * 2) / CAROUSEL_CONFIG.rotationDuration
      this.carouselMesh.rotation.y += delta * rotationSpeed
    }
    
  }
  
  updateFrameScrub(delta) {
    if (!this.framePlane) return
    
    // Smoothly interpolate current progress towards target
    const scrubSpeed = 2.0 // Adjust for faster/slower transitions
    const diff = this.frameTargetProgress - this.frameCurrentProgress
    
    if (Math.abs(diff) > 0.001) {
      this.frameCurrentProgress += diff * scrubSpeed * delta
      this.updateFrameProgress(this.frameCurrentProgress)
    }
  }
  
  updateTextureFade(delta) {
    if (!this.carouselMesh) return
    
    const diff = this.textureFadeTarget - this.textureFadeCurrent
    
    if (Math.abs(diff) > 0.001) {
      const fadeSpeed = 1.0 / TEXTURE_FADE_DURATION
      this.textureFadeCurrent += diff * fadeSpeed * delta
      this.textureFadeCurrent = Math.max(0, Math.min(1, this.textureFadeCurrent))
      
      this.carouselMesh.material.uniforms.textureMix.value = this.textureFadeCurrent
    }
  }
  
  handleFadeIn(isCameraSettled, cameraZ, delta) {
    const cameraAtExperience = Math.abs(cameraZ - EXPERIENCE_CAMERA_Z) < 1.0
    
    if (!isCameraSettled || !cameraAtExperience) {
      this.cameraSettledTime = null
      return
    }
    
    if (this.cameraSettledTime === null) {
      this.cameraSettledTime = performance.now()
      return
    }
    
    const settledDuration = (performance.now() - this.cameraSettledTime) / 1000
    
    if (settledDuration < SETTLE_DELAY) {
      return
    }
    
    // Scale up carousel instances
    if (!this.carouselFadedIn) {
      this.carouselAnimTime += delta
      
      const duration = 0.5
      const progress = Math.max(0, Math.min(1, this.carouselAnimTime / duration))
      
      const dummy = new THREE.Object3D()
      const pos = new THREE.Vector3()
      const quat = new THREE.Quaternion()
      const scale = new THREE.Vector3()
      
      for (let i = 0; i < this.carouselMesh.count; i++) {
        if (this.carouselInstanceScales[i] !== progress) {
          this.carouselInstanceScales[i] = progress
          
          this.carouselBaseMatrices[i].decompose(pos, quat, scale)
          dummy.position.copy(pos)
          dummy.quaternion.copy(quat)
          dummy.scale.set(progress, progress, progress)
          dummy.updateMatrix()
          this.carouselMesh.setMatrixAt(i, dummy.matrix)
        }
      }
      this.carouselMesh.instanceMatrix.needsUpdate = true
      
      if (this.carouselAnimTime >= duration) {
        this.carouselFadedIn = true
      }
    }
    
    // Fade in background plane after carousel scales in
    if (this.carouselFadedIn) {
      this.bgFadeProgress = Math.min(this.bgFadeProgress + delta / FADE_DURATION, 1)
    }
  }
  
  updateBackgroundOpacity(cameraZ) {
    if (!this.backgroundPlane || !this.bgMaterial) return
    
    // Update plane position to follow camera
    const targetZ = cameraZ - this.cameraFollowDistance - this.bgZOffset
    this.backgroundPlane.position.z = targetZ
    
    // Fade in after carousel scales in; fall back to distance-based fade when leaving
    let opacity
    if (this.isActive) {
      opacity = this.bgFadeProgress
    } else {
      opacity = this.updateSectionVisibility(cameraZ)
    }
    
    this.backgroundPlane.visible = opacity > 0.01
    this.bgMaterial.opacity = opacity
  }
  
  onExit() {
    super.onExit()
    console.log('🚪 ExperienceSection.onExit() called')
    
    // Start exit animation
    this.isExiting = true
    this.exitAnimTime = 0
    
    // Reset state when leaving
    this.cameraSettledTime = null
    this.carouselFadedIn = false
    
    // Hide liquid grid when exiting
    if (this.liquidGrid) {
      this.liquidGrid.setVisibility(false)
    }
    
    // Hide toggle
    this.hideToggle()
    
    // Hide experience header
    this.hideExperienceHeader()
    
    // Clear any pending show toggle timeout
    if (this.showToggleTimeout) {
      clearTimeout(this.showToggleTimeout)
      this.showToggleTimeout = null
    }
    
    // Remove mouse listeners when exiting section
    console.log('🎯 Removing mouse listeners')
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseleave', this.onMouseLeave)

  }
  
  handleExitAnimation(delta) {
    if (!this.carouselMesh || !this.carouselBaseMatrices) {
      this.isExiting = false
      return
    }
    
    this.exitAnimTime += delta
    
    const duration = 0.2
    const t = Math.max(0, Math.min(1, this.exitAnimTime / duration))
    
    const dummy = new THREE.Object3D()
    const pos = new THREE.Vector3()
    const quat = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    
    for (let i = 0; i < this.carouselMesh.count; i++) {
      // Interpolate from current scale down to 0 to avoid pops
      const startScale = this.carouselInstanceScales[i] || 1
      const instanceProgress = startScale * (1 - t)
      
      this.carouselBaseMatrices[i].decompose(pos, quat, scale)
      dummy.position.copy(pos)
      dummy.quaternion.copy(quat)
      dummy.scale.set(instanceProgress, instanceProgress, instanceProgress)
      dummy.updateMatrix()
      this.carouselMesh.setMatrixAt(i, dummy.matrix)
    }
    this.carouselMesh.instanceMatrix.needsUpdate = true
    
    if (this.exitAnimTime >= duration) {
      this.isExiting = false
    }
  }
  
  restoreCarouselScales() {
    if (!this.carouselMesh || !this.carouselBaseMatrices) return
    
    const dummy = new THREE.Object3D()
    const pos = new THREE.Vector3()
    const quat = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    
    for (let i = 0; i < this.carouselMesh.count; i++) {
      this.carouselBaseMatrices[i].decompose(pos, quat, scale)
      dummy.position.copy(pos)
      dummy.quaternion.copy(quat)
      dummy.scale.set(1, 1, 1)
      dummy.updateMatrix()
      this.carouselMesh.setMatrixAt(i, dummy.matrix)
    }
    this.carouselMesh.instanceMatrix.needsUpdate = true
    
    // Sync tracking array so future animations start from the right scale
    if (this.carouselInstanceScales) {
      this.carouselInstanceScales.fill(1)
    }
  }
  
  destroy() {
    // Remove event listeners
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseleave', this.onMouseLeave)

    
    // Clean up liquid grid
    if (this.liquidGrid) {
      this.liquidGrid.destroy()
      this.liquidGrid = null
    }
    
    // Clean up frame resources
    if (this.frameAtlasTexture) {
      this.frameAtlasTexture.dispose()
      this.frameAtlasTexture = null
    }
    if (this.frameMaterial) {
      this.frameMaterial.dispose()
      this.frameMaterial = null
    }
    if (this.framePlane) {
      if (this.framePlane.geometry) this.framePlane.geometry.dispose()
      if (this.group) this.group.remove(this.framePlane)
      this.framePlane = null
    }
    
    // Clean up carousel instance data
    this.carouselBaseMatrices = null
    this.carouselInstanceScales = null
    
    // Clean up carousel textures
    if (this.brandsTexture) {
      this.brandsTexture.dispose()
      this.brandsTexture = null
    }
    if (this.personalTexture) {
      this.personalTexture.dispose()
      this.personalTexture = null
    }
    
    // Clear any pending timeout
    if (this.showToggleTimeout) {
      clearTimeout(this.showToggleTimeout)
      this.showToggleTimeout = null
    }

    // Clean up background texture
    if (this.backgroundPlane) {
      if (this.backgroundPlane.geometry) this.backgroundPlane.geometry.dispose()
      if (this.bgMaterial && this.bgMaterial.map) this.bgMaterial.map.dispose()
    }

    super.destroy()
  }
}
